import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { DialogBody, DialogContainer, DialogFooter, DialogHeader, Divider, Message } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from '~/helpers/datetime';
import { showRulesDialog } from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import { isClanOnPreModeration } from '~/helpers/moderation';
import useMount from '~/hooks/useMount';
import { hasPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch, useAppSelector } from '~/store';
import { clanRenameSelector, currentClanSelector } from '~/store/selectors/clanSelectors';
import { currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { getBalanceThunk } from '~/Actions/ActionAccount';
import { checkNameOrTagAvailability } from '~/Actions/ActionClanCreate';
import { actionsClanRename, renameClan, singleCheckRenamingClanStatus } from '~/Actions/ActionClanRename';

import ClanInputWrapper from '~/pages/ClanCreate/ClanInputWrapper';

import styles from './ClanNameEditDialog.scss';
import ClanNameEditFooter from './ClanNameEditFooter';
import ClanNameEditMessages from './ClanNameEditMessages';
import PreModerationNotice from './PreModerationNotice';
import { useClanNameEditState } from './useClanNameEditState';

import type { ClanNameEditDerivedState } from './useClanNameEditState';
import type { IClanRenameStateFields } from '~/Reducers/ReducerClanRename';
import type { IApiError } from '~/types/api';
import type { IDialog } from '~/types/declaration';

const ClanNameEditDialog: React.FC<IDialog> = ({ hideDialog }) => {
  const dispatch = useAppDispatch();

  const clan = useAppSelector(currentClanSelector, shallowEqual);
  const currentAccount = useAppSelector(currentAccountSelector, shallowEqual);
  const { name, tag, isProcessing, error } = useAppSelector(clanRenameSelector, shallowEqual);

  const onPreModeration = clan && isClanOnPreModeration(clan);

  useMount(() => {
    if (!onPreModeration) {
      void dispatch(getBalanceThunk());
    }
  });

  const checkRenamingClanStatus = useCallback(() => dispatch(singleCheckRenamingClanStatus()), [dispatch]);

  const onRenameClanSubmit = useCallback(() => {
    void dispatch(renameClan()).then((isSuccess: boolean) => {
      if (isSuccess) {
        hideDialog();
      }
    });
  }, [dispatch, hideDialog]);

  const onUpdateFieldValue = useCallback(
    (field: keyof IClanRenameStateFields, value: string, fieldError: IApiError) =>
      dispatch(actionsClanRename.updateFieldValueRenamingClan(field, value, fieldError)),
    [dispatch],
  );

  const onValidateOnServer = useCallback(
    (field: keyof IClanRenameStateFields, value: string) => {
      dispatch(actionsClanRename.startCheckAvailabilityRenamingClan(field));
      return dispatch(checkNameOrTagAvailability(field, value, actionsClanRename.finishCheckAvailabilityRenamingClan));
    },
    [dispatch],
  );

  useEffect(() => {
    if (isProcessing) {
      void checkRenamingClanStatus();
    }
  }, [isProcessing, checkRenamingClanStatus]);

  useEffect(() => {
    if (!hasPermission(currentAccount.roleName, PERMISSIONS.RENAME_CLAN)) {
      dispatch(actionsClanRename.stopRenamingClan());
      hideDialog();
    }
  }, [currentAccount, dispatch, hideDialog]);

  const cooldownMessage = useMemo(() => {
    if (settings.clanRename.renameCooldown) {
      const cooldown = currentAccount.nextRenameAvailableAt;
      if (cooldown && !currentAccount.isFreeRenameAvailable) {
        return t('Тег и название были недавно изменены. Повторное изменение станет доступно %(date)s в %(time)s', {
          date: isoToFormattedLocalDate(cooldown),
          time: isoToFormattedLocalTime(cooldown),
        });
      }

      return t('Изменение тега и названия возможно один раз в %(count)s дней.', {
        count: moment.duration(settings.clanRename.renameCooldown, 'seconds').days(),
      });
    }

    return null;
  }, [currentAccount.nextRenameAvailableAt, currentAccount.isFreeRenameAvailable]);

  const { button, values, errors, cost }: ClanNameEditDerivedState = useClanNameEditState({
    clan,
    currentAccount,
    error: error as IApiError,
    cooldownMessage,
    name,
    tag,
  });

  const onShowRules = useCallback(() => dispatch(showRulesDialog()), [dispatch]);

  const buttonProps = useMemo(
    () => ({
      disabled: button.disabled,
      tooltip: button.tooltip,
      loading: isProcessing,
      onSubmit: onRenameClanSubmit,
    }),
    [button.disabled, button.tooltip, isProcessing, onRenameClanSubmit],
  );

  if (onPreModeration) {
    return (
      <DialogContainer>
        <DialogHeader>{t('Тег и название')}</DialogHeader>
        <PreModerationNotice onClose={hideDialog} />
      </DialogContainer>
    );
  }

  return (
    <DialogContainer>
      <DialogHeader>{t('Тег и название')}</DialogHeader>
      <DialogBody adaptiveHeight>
        {!!cooldownMessage && (
          <>
            <div className={styles.headerCooldownMessage}>
              <Message type="error" style={'fullsize'}>
                {cooldownMessage}
              </Message>
            </div>
            <Divider type="major" />
          </>
        )}
        <div>
          <ClanInputWrapper
            className={styles.inputTag}
            isFocusOn
            field="tag"
            value={values.tag}
            isDisabled={isProcessing}
            isValidating={tag.isValidating}
            error={errors.tag || tag.error}
            onChange={onUpdateFieldValue}
            onValidateOnServer={onValidateOnServer}
          />
          <ClanInputWrapper
            className={styles.inputName}
            field="name"
            value={values.name}
            isDisabled={isProcessing}
            isValidating={name.isValidating}
            error={errors.name || name.error}
            onChange={onUpdateFieldValue}
            onValidateOnServer={onValidateOnServer}
          />
        </div>
        <ClanNameEditMessages currentAccount={currentAccount} cost={cost} onShowRules={onShowRules} />
      </DialogBody>
      <DialogFooter>
        <ClanNameEditFooter
          cost={cost}
          button={buttonProps}
          onCancel={hideDialog}
          isLoading={isProcessing || name.isValidating || tag.isValidating}
        />
      </DialogFooter>
    </DialogContainer>
  );
};

export default memo(ClanNameEditDialog);
